import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Mic, MicOff, X, Loader2, Volume2, VolumeX, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_URL = "https://wa.me/447539528505";

// Browser Web Speech API — Chrome/Edge/Safari
const SpeechRecognitionCtor =
  (typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition)) || null;

const LevelMeter = ({ level = 0 }) => {
  // 5 vertical bars whose heights ramp up as level crosses thresholds.
  const bars = [0.15, 0.32, 0.5, 0.68, 0.85];
  return (
    <span className="flex items-end gap-[3px] h-3.5" aria-hidden="true">
      {bars.map((threshold, i) => {
        const active = level >= threshold - 0.15;
        // Scale the bar height by how far past the threshold we are.
        const overshoot = Math.max(0, Math.min(1, (level - threshold + 0.15) / 0.4));
        const heightPct = active ? 40 + overshoot * 60 : 25;
        return (
          <span
            key={i}
            className={`w-[3px] rounded-full transition-[height,background-color,opacity] duration-75 ease-out ${
              active ? "bg-[#D4AF37] opacity-100" : "bg-[#D4AF37]/25 opacity-60"
            }`}
            style={{ height: `${heightPct}%` }}
          />
        );
      })}
    </span>
  );
};

export const VoiceAgent = () => {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hi! I'm Robust Entertainment's voice assistant. Tap the mic or type to ask about our services, and I'll guide you to Paul on WhatsApp for anything specific.",
    },
  ]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [listening, setListening] = useState(false);
  const [muted, setMuted] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [supportedSTT, setSupportedSTT] = useState(true);
  const [isTouch, setIsTouch] = useState(false);
  const [holdActive, setHoldActive] = useState(false);
  const [level, setLevel] = useState(0);

  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const bodyRef = useRef(null);
  const sessionIdRef = useRef(`voice-${Math.random().toString(36).slice(2, 10)}`);
  const transcriptRef = useRef("");
  const modeRef = useRef("toggle"); // "toggle" | "hold"
  const meterStreamRef = useRef(null);
  const meterCtxRef = useRef(null);
  const meterRafRef = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const coarse = window.matchMedia?.("(pointer: coarse)").matches;
    const hasTouch = "ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0;
    setIsTouch(Boolean(coarse || hasTouch));
  }, []);

  useEffect(() => {
    if (!SpeechRecognitionCtor) {
      setSupportedSTT(false);
      return;
    }
    const rec = new SpeechRecognitionCtor();
    rec.lang = "en-GB";
    rec.maxAlternatives = 1;

    rec.onresult = (e) => {
      // Collect any final segments produced in this event
      let finalText = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        if (res.isFinal) finalText += `${res[0].transcript} `;
      }
      finalText = finalText.trim();

      if (modeRef.current === "hold") {
        if (finalText) transcriptRef.current = `${transcriptRef.current} ${finalText}`.trim();
        return; // Wait for release (onend) before sending
      }

      // toggle mode → single-shot send
      const spoken = finalText || e.results[0]?.[0]?.transcript?.trim() || "";
      setListening(false);
      if (spoken) send(spoken);
    };

    rec.onerror = () => {
      setListening(false);
      setHoldActive(false);
      transcriptRef.current = "";
    };
    rec.onend = () => {
      setListening(false);
      setHoldActive(false);
      if (modeRef.current === "hold") {
        const t = transcriptRef.current.trim();
        transcriptRef.current = "";
        if (t) send(t);
      }
    };

    recognitionRef.current = rec;
    return () => {
      try {
        rec.stop();
      } catch (_) { /* ignore */ }
    };
  }, []);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, thinking]);

  // Collapse the pill to a compact icon once the user scrolls past the hero
  useEffect(() => {
    const threshold = () => Math.max(window.innerHeight * 0.85, 500);
    const onScroll = () => setCollapsed(window.scrollY > threshold());
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Live speech-level meter — activates whenever recognition is active.
  useEffect(() => {
    let cancelled = false;

    const stopMeter = () => {
      if (meterRafRef.current) cancelAnimationFrame(meterRafRef.current);
      meterRafRef.current = 0;
      setLevel(0);
      try {
        meterStreamRef.current?.getTracks().forEach((t) => t.stop());
      } catch (_) { /* ignore */ }
      try {
        meterCtxRef.current?.close();
      } catch (_) { /* ignore */ }
      meterStreamRef.current = null;
      meterCtxRef.current = null;
    };

    const startMeter = async () => {
      if (!navigator.mediaDevices?.getUserMedia) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        meterStreamRef.current = stream;
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        meterCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.5;
        source.connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);

        const tick = () => {
          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i++) {
            const v = (data[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / data.length);
          setLevel(Math.min(1, rms * 4.5));
          meterRafRef.current = requestAnimationFrame(tick);
        };
        tick();
      } catch (_) { /* ignore */ }
    };

    if (listening) startMeter();
    else stopMeter();

    return () => {
      cancelled = true;
      stopMeter();
    };
  }, [listening]);

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setSpeaking(false);
  };

  const speak = async (text) => {
    if (muted) return;
    try {
      stopSpeaking();
      const res = await axios.post(
        `${API}/voice/tts`,
        { text },
        { responseType: "blob", timeout: 60000 }
      );
      const blob = new Blob([res.data], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setSpeaking(false);
        URL.revokeObjectURL(url);
      };
      setSpeaking(true);
      await audio.play();
    } catch (err) {
      setSpeaking(false);
    }
  };

  const send = async (text) => {
    const clean = (text || "").trim();
    if (!clean || thinking) return;

    const history = messages.map((m) => ({ role: m.role === "assistant" ? "assistant" : "user", text: m.text }));
    setMessages((m) => [...m, { role: "user", text: clean }]);
    setInput("");
    setThinking(true);

    try {
      const res = await axios.post(
        `${API}/voice/chat`,
        { message: clean, session_id: sessionIdRef.current, history },
        { timeout: 45000 }
      );
      const reply = res.data.reply;
      setMessages((m) => [...m, { role: "assistant", text: reply }]);
      speak(reply);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text:
            "Sorry, I couldn't answer that just now. Please message Paul directly on WhatsApp on 07539 528 505.",
        },
      ]);
    } finally {
      setThinking(false);
    }
  };

  const toggleListen = () => {
    if (!recognitionRef.current || thinking) return;
    const rec = recognitionRef.current;
    if (listening) {
      try { rec.stop(); } catch (_) { /* ignore */ }
      setListening(false);
      return;
    }
    stopSpeaking();
    try {
      modeRef.current = "toggle";
      rec.continuous = false;
      rec.interimResults = false;
      transcriptRef.current = "";
      rec.start();
      setListening(true);
    } catch (_) {
      setListening(false);
    }
  };

  const startHold = () => {
    if (!recognitionRef.current || thinking || listening) return;
    stopSpeaking();
    try {
      modeRef.current = "hold";
      const rec = recognitionRef.current;
      rec.continuous = true;
      rec.interimResults = true;
      transcriptRef.current = "";
      rec.start();
      setListening(true);
      setHoldActive(true);
    } catch (_) {
      setHoldActive(false);
      setListening(false);
    }
  };

  const endHold = () => {
    if (!holdActive) return;
    setHoldActive(false);
    try {
      recognitionRef.current?.stop();
    } catch (_) { /* ignore */ }
    // onend will fire the send with the accumulated transcript
  };

  const cancelHold = () => {
    if (!holdActive) return;
    // Cancel WITHOUT sending: null out transcript first, then stop
    transcriptRef.current = "";
    setHoldActive(false);
    try {
      recognitionRef.current?.abort?.() || recognitionRef.current?.stop();
    } catch (_) { /* ignore */ }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    send(input);
  };

  return (
    <>
      {/* Floating trigger button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="voice-agent-toggle"
        aria-label={open ? "Close voice assistant" : "Chat with us"}
        className={`fixed bottom-6 right-6 z-40 flex items-center gap-2.5 h-14 rounded-full bg-[#0D0D0D] border border-[#D4AF37]/70 shadow-[0_0_28px_rgba(212,175,55,0.35)] hover:shadow-[0_0_36px_rgba(212,175,55,0.7)] transition-all duration-300 group ${
          open || !collapsed ? "px-5" : "px-0 w-14 justify-center"
        }`}
      >
        {open ? (
          <>
            <X size={20} className="text-[#D4AF37]" />
            <span className="text-sm font-semibold tracking-wide text-gold uppercase">Close</span>
          </>
        ) : (
          <>
            <span className="relative flex items-center justify-center">
              <Mic size={20} className="text-[#D4AF37] group-hover:scale-110 transition-transform" />
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#D4AF37] animate-pulse" />
            </span>
            <span
              className={`text-sm font-semibold tracking-wide text-gold uppercase whitespace-nowrap overflow-hidden transition-all duration-300 ${
                collapsed ? "max-w-0 opacity-0" : "max-w-[180px] opacity-100"
              }`}
            >
              Chat With Us
            </span>
          </>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="voice-agent-panel"
          className="fixed bottom-24 right-6 z-40 w-[92vw] sm:w-[400px] max-h-[70vh] flex flex-col rounded-2xl bg-[#0D0D0D] border border-[#D4AF37]/40 shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-gradient-to-r from-[#0D0D0D] to-[#1a1408]">
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-gold font-semibold">Voice Assistant</p>
              <p className="text-sm text-white font-medium">Ask about Robust Entertainment</p>
            </div>
            <button
              type="button"
              onClick={() => setMuted((m) => !m)}
              className="p-2 rounded-full hover:bg-white/5 text-zinc-300 transition-colors"
              aria-label={muted ? "Unmute" : "Mute"}
              data-testid="voice-agent-mute"
            >
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
          </div>

          {/* Messages */}
          <div
            ref={bodyRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#0a0a0a]"
            data-testid="voice-agent-messages"
          >
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#FF2A2A] text-white rounded-br-md"
                      : "bg-[#181818] text-zinc-100 border border-white/5 rounded-bl-md"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {thinking && (
              <div className="flex justify-start">
                <div className="bg-[#181818] border border-white/5 rounded-2xl rounded-bl-md px-4 py-2.5 flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin text-[#D4AF37]" />
                  <span className="text-xs text-zinc-400">Thinking...</span>
                </div>
              </div>
            )}
          </div>

          {/* WhatsApp CTA */}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="voice-agent-whatsapp"
            className="flex items-center justify-center gap-2 py-3 border-t border-b border-[#D4AF37]/30 bg-gradient-to-r from-[#0D0D0D] via-[#1a1408] to-[#0D0D0D] text-sm text-gold font-semibold hover:text-white transition-colors"
          >
            <MessageCircle size={16} />
            Continue on WhatsApp — 07539 528 505
          </a>

          {/* Input */}
          <form onSubmit={handleSubmit} className="relative flex items-center gap-2 p-3 bg-[#0D0D0D]">
            {holdActive && (
              <div
                className="absolute -top-9 left-3 right-3 rounded-full border border-[#FF2A2A]/60 bg-black/85 backdrop-blur-sm px-3 py-1.5 flex items-center justify-center gap-2.5 text-[10px] tracking-[0.2em] uppercase text-[#FF6B6B] font-semibold shadow-[0_8px_20px_rgba(0,0,0,0.35)]"
                data-testid="voice-agent-hold-hint"
              >
                <LevelMeter level={level} />
                Recording · Release to send · Slide off to cancel
              </div>
            )}
            {listening && !holdActive && (
              <div
                className="absolute -top-9 left-3 rounded-full border border-[#D4AF37]/50 bg-black/85 backdrop-blur-sm px-3 py-1.5 flex items-center gap-2.5 text-[10px] tracking-[0.2em] uppercase text-gold font-semibold shadow-[0_8px_20px_rgba(0,0,0,0.35)]"
                data-testid="voice-agent-listen-hint"
              >
                <LevelMeter level={level} />
                Listening
              </div>
            )}
            <button
              type="button"
              onClick={isTouch ? undefined : toggleListen}
              onPointerDown={isTouch ? (e) => { e.preventDefault(); startHold(); } : undefined}
              onPointerUp={isTouch ? endHold : undefined}
              onPointerCancel={isTouch ? cancelHold : undefined}
              onPointerLeave={isTouch && holdActive ? cancelHold : undefined}
              onContextMenu={isTouch ? (e) => e.preventDefault() : undefined}
              disabled={!supportedSTT || thinking}
              data-testid="voice-agent-mic"
              style={
                listening
                  ? {
                      boxShadow: `0 0 0 ${4 + level * 14}px rgba(212,175,55,${0.15 + level * 0.35}), 0 0 ${18 + level * 24}px rgba(255,42,42,${0.2 + level * 0.35})`,
                    }
                  : undefined
              }
              className={`relative flex items-center justify-center w-11 h-11 rounded-full shrink-0 border transition-colors duration-200 select-none touch-none ${
                listening
                  ? "bg-[#FF2A2A] border-[#FF2A2A] text-white"
                  : "bg-[#121212] border-[#D4AF37]/50 text-[#D4AF37] hover:border-[#D4AF37]"
              } disabled:opacity-40 disabled:cursor-not-allowed`}
              aria-label={
                !supportedSTT
                  ? "Voice input not supported"
                  : isTouch
                  ? holdActive
                    ? "Release to send, slide away to cancel"
                    : "Press and hold to talk"
                  : listening
                  ? "Stop listening"
                  : "Start listening"
              }
              title={
                supportedSTT
                  ? isTouch
                    ? "Press and hold to talk"
                    : "Tap to talk"
                  : "Voice input not supported in this browser"
              }
            >
              {listening ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                holdActive
                  ? "Listening... release to send"
                  : listening
                  ? "Listening..."
                  : isTouch
                  ? "Type or hold the mic"
                  : "Type or tap the mic"
              }
              data-testid="voice-agent-input"
              className="flex-1 bg-[#121212] border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors"
            />
            <button
              type="submit"
              disabled={!input.trim() || thinking}
              data-testid="voice-agent-send"
              className="text-sm font-semibold text-[#D4AF37] hover:text-white px-3 disabled:opacity-30 transition-colors"
            >
              Send
            </button>
          </form>
          {speaking && (
            <button
              type="button"
              onClick={stopSpeaking}
              data-testid="voice-agent-stop-audio"
              className="absolute top-3 right-16 text-[10px] uppercase tracking-widest text-gold hover:text-white"
            >
              Stop audio
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default VoiceAgent;
